import { useCallback, useEffect, useState } from 'react';

import type { UUID } from '@aline/common';
import type { params } from '@aline/routes';
import { routes } from '@aline/routes';
import { useLocation, useMatch, useParams } from 'react-router-dom';
import { useCurrentTabId } from 'src/layouts/tabs/tab-active-context';
import { useTabContext } from 'src/layouts/tabs/tab-context';

/**
 * One arrival of a one-shot deep-link param. `deliveryId` increments per arrival, so re-triggering
 * the same deep link is still a distinct event — an effect keyed on the delivery re-runs, where a
 * bare string would have been swallowed by React's state bail-out.
 */
export interface PendingParamDelivery {
  deliveryId: number;
  value: string;
}

/**
 * `[arrival, acknowledgeArrival]`. The arrival stays put until acknowledged, so a consumer that
 * has to wait for something (an editor to finish loading, a query to resolve) can act on a later
 * render — see `useConsumePendingParam` for why acknowledging is mandatory.
 */
export type PendingParamConsumer = readonly [PendingParamDelivery | null, () => void];

/**
 * Captures a one-shot deep-link query param from the current tab's `pendingParams` (ephemeral keys
 * split off `tab.route` by TabProvider).
 *
 * CALLERS MUST CALL `acknowledgeArrival()` once they have acted on the arrival. Reading it is not
 * enough: tab panes stay mounted inside `React.Activity`, which preserves state but tears effects
 * down on hide and re-runs them on re-show. An unacknowledged arrival is still sitting in state at
 * that point, so every effect keyed on it fires again — that is a deep link replaying its scroll
 * and highlight on every single tab switch, forever. Acknowledging drops the arrival, which both
 * ends the replay and leaves the hook ready for the next deep link.
 *
 * Deleting the key from the tab's `pendingParams` (`consumePendingParam`) only stops a SECOND read
 * of the store. It cannot stop a replay of the value this hook already handed out.
 *
 * Depends on `useLocation().search` (which merges pending into the durable route) so the
 * effect re-runs when pending params arrive after mount (e.g. location sync on cold load).
 */
export function useConsumePendingParam(paramKey: string): PendingParamConsumer {
  const location = useLocation();
  const tabId = useCurrentTabId();
  const { consumePendingParam } = useTabContext();
  const [delivery, setDelivery] = useState<PendingParamDelivery | null>(null);
  const searchValue = new URLSearchParams(location.search).get(paramKey);
  const acknowledgeArrival = useCallback(() => setDelivery(null), []);

  useEffect(() => {
    if (!tabId || !searchValue) return;

    const value = consumePendingParam(tabId, paramKey);
    if (value === null) return;

    setDelivery(previous => ({ deliveryId: (previous?.deliveryId ?? 0) + 1, value }));
  }, [consumePendingParam, paramKey, searchValue, tabId]);

  return [delivery, acknowledgeArrival];
}

interface DocumentParams {
  documentId: UUID;
}
export function useDocumentParams(): DocumentParams {
  const params = useParams<{ documentId: string }>();
  if (!params.documentId) throw new Error('Missing documentId parameter');
  return { documentId: params.documentId };
}

interface RequestAccessToDocumentParams {
  documentId: UUID;
}
export function useRequestAccessToDocumentParams(): RequestAccessToDocumentParams {
  const params = useParams<{ documentId: string }>();
  if (!params.documentId) throw new Error('Missing documentId parameter');
  return { documentId: params.documentId };
}

interface MaybeDocumentParams {
  documentId?: UUID;
}
export function useMaybeDocumentParams(): MaybeDocumentParams {
  return useParams<{ documentId?: string }>();
}

/**
 * Reads `documentId` from the URL pathname directly (matching `/doc/<uuid>`),
 * independent of the nearest matched route. Use this in components that live
 * ABOVE the `/doc/:documentId/*` match — e.g., the global drag-and-drop
 * overlay mounted in `AppLayout` — where `useParams()` returns an empty object
 * because the consumer is outside the Route that binds the param. Within the
 * Editor tab itself, prefer `useMaybeDocumentParams()`.
 */
export function useDocumentIdFromUrl(): UUID | null {
  const match = useMatch({ end: false, path: routes.DocumentDetail });
  return match?.params.documentId ?? null;
}

interface MaybeWorkflowParams {
  documentId: UUID;
  workflowId?: UUID;
}
export function useMaybeWorkflowParams(): MaybeWorkflowParams {
  const params = useParams<{ documentId?: string; workflowId?: string }>();
  if (!params.documentId) throw new Error('Missing documentId parameter');
  return { documentId: params.documentId, workflowId: params.workflowId };
}

interface VersionHistoryParams {
  documentId: UUID;
  versionId: UUID;
}
export function useVersionHistoryParams(): VersionHistoryParams {
  const params = useParams<{ documentId?: string; versionId?: string }>();
  if (!params.documentId) throw new Error('Missing documentId parameter');
  if (!params.versionId) throw new Error('Missing versionId parameter');
  return { documentId: params.documentId, versionId: params.versionId };
}

export function useIsAtVersionHistoryRoute() {
  const versionHistoryMatch = useMatch({ end: false, path: routes.DocumentDetailVersionIndex });
  return { isAtVersionHistoryRoute: Boolean(versionHistoryMatch) };
}

interface WorkflowParams {
  workflowId: UUID;
}
export function useWorkflowParams(): WorkflowParams {
  const params = useParams<{ workflowId?: string }>();
  if (!params.workflowId) throw new Error('Missing workflowId parameter');
  return { workflowId: params.workflowId };
}

interface ReportRouteParams {
  reportId: UUID;
}
export function useReportParams(): ReportRouteParams {
  const params = useParams<{ reportId?: string }>();
  if (!params.reportId) throw new Error('Missing reportId parameter');
  return { reportId: params.reportId };
}

interface GroupParams {
  groupId: UUID;
}
export function useGroupParams(): GroupParams {
  const params = useParams<{ groupId?: string }>();
  if (!params.groupId) throw new Error('Missing groupId parameter');
  return { groupId: params.groupId };
}

interface PartyParams {
  partyId: UUID;
}
export function usePartyParams(): PartyParams {
  const params = useParams<{ partyId?: string }>();
  if (!params.partyId) throw new Error('Missing partyId parameter');
  return { partyId: params.partyId };
}

interface CommunityParams {
  // Unique title of category (e.g., sales, startups, corporate-legal).
  [params.categoryId]?: string;
}

export function useMaybeCommunityParams(): CommunityParams {
  return useParams<{ categoryId?: string }>();
}

interface MaybeReminderParams {
  reminderId?: UUID;
}
export function useMaybeReminderParams(): MaybeReminderParams {
  const routeMatch = useMatch(routes.RemindersDetail);
  return { reminderId: routeMatch?.params.reminderId };
}
